import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';
import { SeoService } from '../../core/services/seo.service';
import { WhatsappService } from '../../core/services/whatsapp.service';
import { AuthService } from '../../core/services/auth.service';
import { CategoriaService } from '../../core/services/categoria.service';
import { FiltroPaisService } from '../../core/services/filtro-pais.service';
import { VacantesService, SugerenciaUbicacion } from '../../core/services/vacantes.service';
import { Categoria, Vacante, VacanteFiltros } from '../../core/models/vacante.model';
import { detectDefaultPais, PaisCatalogo } from '../../core/models/catalogos';
import { resolverFiltrosANavegacion } from '../../core/util/vacante-search-nav';
import { paisSlug } from '../../core/utils/vacante-labels';
import { environment } from '../../../environments/environment';
import { JobCardComponent } from '../../shared/components/job-card/job-card.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';

interface Testimonio {
  nombre: string;
  puesto: string;
  ciudad: string;
  texto: string;
}

interface Plan {
  nombre: string;
  precio: string;
  moneda: string;
  descripcion: string;
  destacado: boolean;
  features: string[];
  cta: string;
}

interface FaqItem {
  pregunta: string;
  respuesta: string;
  open: boolean;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, JobCardComponent, LoginModalComponent],
  templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
  private seo = inject(SeoService);
  private whatsapp = inject(WhatsappService);
  private auth = inject(AuthService);
  private categoriaService = inject(CategoriaService);
  private filtroPaisService = inject(FiltroPaisService);
  private vacantesService = inject(VacantesService);
  private router = inject(Router);

  categorias: Categoria[] = [];
  paises: PaisCatalogo[] = [];
  whatsappLink = this.whatsapp.registerLink();

  filtros: VacanteFiltros = {
    q: '',
    sector: undefined,
    ciudad: '',
    pais: '',
    modalidad: undefined,
  };

  sugerenciasUbicacion = signal<SugerenciaUbicacion[]>([]);
  private ciudadSearchSubject = new Subject<string>();
  private ocultarSugerenciasTimeout?: ReturnType<typeof setTimeout>;

  vacantesDestacadas = signal<Vacante[]>([]);
  cargandoVacantes = signal(true);

  loginModalOpen = signal(false);

  metricas = [
    { valor: '50K+', etiqueta: 'Candidatos activos' },
    { valor: '200+', etiqueta: 'Empresas confían en nosotros' },
    { valor: '5K+', etiqueta: 'Vacantes publicadas al mes' },
  ];

  pasos = [
    {
      icono: 'forum',
      titulo: '1. Cuéntanos qué sabes hacer',
      descripcion: 'Manda tu CV en PDF o Word, una foto, un audio o solo escríbelo por WhatsApp. Nuestra IA entiende lo que necesitas, sin comandos ni registros pesados.',
      color: 'primary',
    },
    {
      icono: 'auto_awesome',
      titulo: '2. La IA te busca vacantes',
      descripcion: 'Nuestra inteligencia artificial encuentra las vacantes que mejor encajan con tu perfil. 🤖',
      color: 'secondary',
    },
    {
      icono: 'send',
      titulo: '3. Postula desde WhatsApp',
      descripcion: 'Con un solo tap envías tu perfil a la empresa y damos seguimiento a tu postulación.',
      color: 'whatsapp',
    },
  ];

  testimonios: Testimonio[] = [
    {
      nombre: 'Ricardo Martínez',
      puesto: 'Almacenista',
      ciudad: 'CDMX',
      texto:
        'Chambia me cambió la vida. En menos de 2 días ya tenía una entrevista por WhatsApp y hoy ya tengo mi contrato.',
    },
    {
      nombre: 'Daniela Gómez',
      puesto: 'Ejecutiva de ventas',
      ciudad: 'Bogotá',
      texto: 'Postulé sin usar computadora, todo desde mi celular. La IA me recomendó vacantes que sí encajaban conmigo.',
    },
    {
      nombre: 'Luis Fernández',
      puesto: 'Soldador',
      ciudad: 'Guadalajara',
      texto: 'Antes batallaba para armar un CV. Con Chambia solo grabé un audio contando mi experiencia y listo.',
    },
  ];

  planes: Plan[] = [
    {
      nombre: 'Básico',
      precio: '0',
      moneda: 'MXN/mes',
      descripcion: 'Para empezar a explorar oportunidades.',
      destacado: false,
      features: [
        '3 postulaciones al mes',
        'Perfil básico en WhatsApp',
        'Notificaciones de nuevas vacantes',
      ],
      cta: 'Elegir Básico',
    },
    {
      nombre: 'Pro',
      precio: '149',
      moneda: 'MXN/mes',
      descripcion: 'Encuentra chamba 3x más rápido.',
      destacado: true,
      features: [
        'Postulaciones ilimitadas',
        'Feedback de IA en entrevistas',
        'Badge de "Perfil Verificado"',
        'Prioridad en el matching',
      ],
      cta: 'Acelerar búsqueda',
    },
    {
      nombre: 'Premium',
      precio: '299',
      moneda: 'MXN/mes',
      descripcion: 'Acompañamiento VIP para tu carrera.',
      destacado: false,
      features: [
        'Todo lo de Pro',
        'Mentoría 1-a-1 vía chat',
        'Revisión de CV con IA',
      ],
      cta: 'Ver beneficios VIP',
    },
  ];

  faqs: FaqItem[] = [
    {
      pregunta: '¿Es gratis para los candidatos?',
      respuesta:
        'Sí, usar Chambia para buscar y postular a vacantes es completamente gratis. También tenemos planes Pro opcionales para acelerar tu búsqueda con herramientas de IA.',
      open: false,
    },
    {
      pregunta: '¿Cómo funciona la entrevista o el matching por WhatsApp?',
      respuesta:
        'Nuestra IA te hace algunas preguntas clave sobre tu experiencia directamente en el chat, igual que si hablaras con un amigo, y te sugiere vacantes acordes a tu perfil.',
      open: false,
    },
    {
      pregunta: '¿Qué tipo de trabajos hay disponibles?',
      respuesta:
        'Desde puestos operativos en almacenes, cocinas y construcción, hasta roles administrativos, de ventas y tecnología en toda Latinoamérica.',
      open: false,
    },
    {
      pregunta: '¿Es seguro compartir mis datos con Chambia?',
      respuesta:
        'Sí. Cumplimos con la normativa de protección de datos vigente y solo compartimos tu perfil con empresas verificadas en la plataforma.',
      open: false,
    },
  ];

  ngOnInit(): void {
    this.vacantesService.obtenerPaises().subscribe((p) => {
      this.paises = p;
      // "Todos los países" elegido antes (p.ej. en Vacantes) se respeta: no
      // se reinyecta el país detectado del navegador (WEB-01).
      if (!this.filtros.pais && !this.filtroPaisService.todosLosPaises()) {
        this.filtros.pais = detectDefaultPais(p)?.code ?? '';
      }
      // La primera búsqueda de destacadas se ejecuta después de resolver el
      // país default: el selector y el request a /public/vacantes deben
      // corresponder desde la primera carga (antes se buscaba sin país y se
      // mezclaban vacantes de todos los países).
      this.cargarVacantesDestacadas();
    });
    this.categoriaService.listar().subscribe((cats) => (this.categorias = cats));

    // Sin distinctUntilChanged: si el listado se ocultó (blur) y el campo se
    // vuelve a enfocar con el mismo texto, el subject debe volver a emitir
    // para reabrir el dropdown (WEB-01).
    this.ciudadSearchSubject.pipe(debounceTime(300)).subscribe((q) => {
      // "Todos los países" (pais vacío) se respeta tal cual: NO se sustituye
      // por el país detectado del navegador (WEB-01).
      const paisBuscado = this.filtros.pais ?? '';
      this.vacantesService.ubicaciones(paisBuscado, q).subscribe((sugerencias) => {
        // Si el país o el texto cambiaron mientras la respuesta estaba en vuelo,
        // las sugerencias ya no corresponden y se descartan para no reinsertar
        // combinaciones país-ciudad inválidas (WEB-01).
        const paisActual = this.filtros.pais ?? '';
        if (paisActual !== paisBuscado || this.filtros.ciudad !== q) return;
        this.sugerenciasUbicacion.set(sugerencias);
      });
    });

    this.seo.setMetaTags({
      title: 'Vacantes de empleo en LATAM — Chambia',
      description:
        'Busca miles de vacantes por sector, ciudad y modalidad en toda Latinoamérica. Regístrate gratis por WhatsApp y postula con ayuda de IA.',
      path: '/',
    });
  }

  ngOnDestroy(): void {
    this.seo.removeJsonLd('organization-schema-home');
    this.seo.removeJsonLd('vacantes-destacadas-home');
  }

  /** Primera carga de vacantes destacadas, siempre con el país default ya resuelto. */
  private cargarVacantesDestacadas(): void {
    this.vacantesService
      .buscar({ pais: this.filtros.pais, sortBy: 'recencia', page: 0, size: 8 })
      .subscribe((res) => {
        this.vacantesDestacadas.set(res.items);
        this.cargandoVacantes.set(false);
        if (res.items.length) {
          this.seo.setJsonLd('vacantes-destacadas-home', this.itemListSchema(res.items));
        }
      });
  }

  buscarChamba(): void {
    const { path, queryParams } = resolverFiltrosANavegacion(this.filtros, this.categorias, this.paises);
    this.router.navigate(path, { queryParams });
  }

  onCiudadInput(value: string): void {
    this.filtros.ciudad = value;
    this.ciudadSearchSubject.next(value);
  }

  /**
   * Al cambiar de país, una ciudad ya escrita puede pertenecer a otro país —
   * se limpia para no buscar combinaciones incorrectas (ej. Bogotá + México).
   * Se limpia también con "Todos los países" para que la ciudad nunca quede
   * emparejada con un país distinto al suyo. Las sugerencias se limpian
   * siempre (aunque la ciudad esté vacía): el dropdown pudo quedar abierto
   * con sugerencias del país anterior.
   */
  onPaisChange(pais: string): void {
    // La elección se registra en el servicio para que la sincronización con
    // la URL en Vacantes respete "Todos los países" (WEB-01).
    this.filtroPaisService.registrar(pais);
    this.filtros.pais = pais;
    this.filtros.ciudad = '';
    this.sugerenciasUbicacion.set([]);
  }

  onCiudadFocus(): void {
    // Abrir el listado también con el campo vacío: la consulta se filtra por
    // el país actual, o por ninguno si está seleccionado "Todos los países".
    // Antes solo se buscaba si ya había texto, por lo que tras seleccionar
    // un país (que limpia la ciudad) el dropdown nunca volvía a abrirse.
    this.ciudadSearchSubject.next(this.filtros.ciudad || '');
  }

  /** Delay para que el (click) de una sugerencia alcance a disparar antes de que el blur la oculte. */
  ocultarSugerenciasConDelay(): void {
    this.ocultarSugerenciasTimeout = setTimeout(() => this.sugerenciasUbicacion.set([]), 150);
  }

  elegirUbicacion(s: SugerenciaUbicacion): void {
    if (this.ocultarSugerenciasTimeout) clearTimeout(this.ocultarSugerenciasTimeout);
    this.filtros.ciudad = s.etiqueta;
    this.sugerenciasUbicacion.set([]);
  }

  /**
   * "Regístrate gratis y postula": un candidato con sesión activa no debe
   * volver a pasar por el login — continúa directo al flujo de postulación
   * (directorio de vacantes). El modal solo aplica sin sesión.
   */
  openLogin(): void {
    if (this.auth.isLoggedIn) {
      this.router.navigate(['/vacantes']);
      return;
    }
    this.loginModalOpen.set(true);
  }

  closeLogin(): void {
    this.loginModalOpen.set(false);
  }

  private itemListSchema(vacantes: Vacante[]): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: vacantes.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${environment.siteUrl}/vacantes/${paisSlug(v.pais)}/${v.urlSlug}`,
      })),
    };
  }

  toggleFaq(item: FaqItem): void {
    item.open = !item.open;
  }
}
